/* visually inspired by https://v2.vuejs.org/v2/examples/modal, functionality is custom */

<script lang="ts">
import { ModalType, ActionTakenType, useModalControlStore } from '@/stores/modalControl'

export default {
  name: 'Modal',
  computed: {
    modalType() {
      if (this.store.modalData.type === ModalType.Error) {
        return 'error'
      } else if (this.store.modalData.type === ModalType.Information) {
        return 'information'
      } else if (this.store.modalData.type === ModalType.Confirmation) {
        return 'confirmation'
      } else if (this.store.modalData.type === ModalType.Input) {
        return 'input'
      }
    }
  },
  methods: {
    confirm() {
      this.store.modalData.actionTaken = ActionTakenType.Confirm
      if (this.store.modalData.confirmButtonFunction)
        this.store.modalData.confirmButtonFunction(this.store.modalData)
      this.store.closeModal()
    },
    cancel() {
      this.store.modalData.actionTaken = ActionTakenType.Cancel
      if (this.store.modalData.cancelButtonFunction)
        this.store.modalData.cancelButtonFunction(this.store.modalData)
      this.store.closeModal()
    }
  },
  setup() {
    const store = useModalControlStore()
    return { store }
  }
}
</script>

<template>
  <Transition name="modal">
    <div v-if="store.showModalInner" class="modal-mask">
      <div class="modal-wrapper">
        <v-container
          class="modal-container"
          :style="{ maxWidth: store.modalData.modalWidth ?? '400px' }"
        >
          <v-row>
            <v-col id="modalHeader" :class="modalType">
              <h3 v-text="store.modalData.header" />
            </v-col>
            <v-col cols="1">
              <button
                id="cancelModal"
                type="submit"
                @click="cancel"
                style="float: right; margin-left: auto; margin-right: 0"
              >
                <font-awesome-icon icon="fa-x" />
              </button>
            </v-col>
          </v-row>

          <v-row v-if="store.modalData.message">
            <v-col class="d-flex justify-center align-center">
              <p v-html="store.modalData.message" class="scrollable-message" />
            </v-col>
          </v-row>

          <v-row v-if="store.modalData.tableMessage">
            <v-col class="d-flex justify-center align-center">
              <div style="display: grid; grid-template-columns: 1fr 2fr">
                <template v-for="(dat, lab) in store.modalData.tableMessage" :key="lab">
                  <div style="font-weight: bold; margin-right: 8px; justify-self: end">
                    {{ lab }}:
                  </div>
                  <div v-if="dat[0] !== '<'">{{ dat }}</div>
                  <div v-else v-html="dat" />
                </template>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="store.modalData.component">
            <v-col class="d-flex justify-center align-center">
              <component
                :is="store.modalData.component"
                v-bind="store.modalData.componentProps"
                @close="cancel"
              />
            </v-col>
          </v-row>

          <v-row v-if="store.modalData.linkToPdf">
            <v-col class="d-flex justify-center align-center">
              <embed
                :src="store.modalData.linkToPdf"
                type="application/pdf"
                width="100%"
                height="800px"
              />
            </v-col>
          </v-row>

          <v-row v-if="modalType === 'input'">
            <v-col class="d-flex justify-center align-center">
              <v-textarea v-model="store.modalData.inputValue" label="Input" rows="3" outlined />
            </v-col>
          </v-row>

          <v-row v-if="!store.modalData.hideActions" class="modal-footer">
            <v-col v-if="modalType === 'confirmation' || modalType === 'input'">
              <span>
                <v-btn
                  id="confirmBtn"
                  name="confirmBtn"
                  class="modal-button"
                  @click="confirm"
                  color="confirm"
                >
                  {{ store.modalData.confirmButtonText }}
                </v-btn>
              </span>

              <span sytle="float: right">
                <v-btn
                  style=""
                  id="cancel-button"
                  name="cancel-button"
                  class="modal-button-secondary"
                  color="cancel"
                  @click="cancel"
                >
                  {{ store.modalData.cancelButtonText }}
                </v-btn>
              </span>
            </v-col>
            <v-col v-else>
              <v-btn
                id="confirmBtn"
                name="confirmBtn"
                class="modal-button"
                color="primary"
                @click="confirm()"
              >
                {{ store.modalData.confirmButtonText }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </div>
  </Transition>
</template>

<style>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  max-width: 350px;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  font-family: Helvetica, Arial, sans-serif;
}

.modal-header h3 {
  margin-top: 0;
}

.information {
  color: var(--color-cu-gold);
}

.error {
  color: red;
}

.modal-body {
  margin: 20px 0;
  width: 300px;
  overflow-wrap: anywhere;
}

.modal-button-secondary {
  float: right;
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
.scrollable-message {
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
  word-break: break-word;
}
</style>
